import type { BookingSlot, BookingSlotsDay } from '../../model/slots-management/interfaces'
import type { ApiResponse } from '@shared/api'

import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useSlotsManagementStore } from '../../model/slots-management/store'

const nullableToInput = (value: number | string | null | undefined): number | string =>
  value === null || value === undefined ? '' : value

const inputToNullableInt = (value: number | string): number | null =>
  value === '' ? null : Number(value)

const normalizeDay = (day: BookingSlotsDay): BookingSlotsDay => ({
  day_of_week: day.day_of_week,
  slots: day.slots.map((slot): BookingSlot => ({
    ...slot,
    enable_slot: slot.enable_slot ?? slot.enable_limit ?? false,
    limit: nullableToInput(slot.limit),
    pre_shift_limit: nullableToInput(slot.pre_shift_limit),
    sales_max: nullableToInput(slot.sales_max),
    disabled_timezones: slot.disabled_timezones ?? [],
  })),
})

export const useBookingSlotsApi = async (): Promise<boolean> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const store = useSlotsManagementStore()
  const commonStore = useCommonStore()

  const url = useAdminRoutes('admin.demo-slots-management.data')

  commonStore.setLoading(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return false
    }

    const { data } = await $axios.get<ApiResponse>(url)

    if (!data.success) {
      toast.error(data.message)
      return false
    }

    const days: BookingSlotsDay[] = (data.data?.days_of_week ?? []).map(normalizeDay)
    store.setBookingDays(days)

    return true
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return false
  } finally {
    commonStore.setLoading(false)
  }
}

export const useBookingSlotsSave = async (): Promise<boolean> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const store = useSlotsManagementStore()

  const url = useAdminRoutes('admin.demo-slots-management.update')

  if (!url) {
    toast.error('Unexpected error. Invalid request URL.')
    return false
  }

  const daysOfWeek = store.getBookingDays.map(day => ({
    day_of_week: day.day_of_week,
    slots: day.slots.map(slot => ({
      time: slot.time,
      enable_slot: slot.enable_slot,
      enable_limit: slot.enable_slot,
      limit: inputToNullableInt(slot.limit),
      pre_shift_limit: inputToNullableInt(slot.pre_shift_limit),
      sales_max: inputToNullableInt(slot.sales_max),
      disabled_timezones: slot.disabled_timezones,
    })),
  }))

  store.setSaving(true)

  try {
    const { data } = await $axios.put<ApiResponse>(url, { schema: { days_of_week: daysOfWeek } })

    if (!data.success) {
      toast.error(data.message)
      return false
    }

    toast.success(data.message)

    return true
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return false
  } finally {
    store.setSaving(false)
  }
}
